import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import UserSidebar from "@/components/UserSidebar";
import { useAuth } from "@/components/AuthContext";
import { Mail, Phone, Calendar, CheckCircle, XCircle, Edit2, User, Send, Users, Copy, Share2, Gift, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { updateEmail, sendPhoneVerification, verifyPhoneCode, fetchReferralInfo, fetchCouponStatus, redeemCoupon, ReferralInfo } from "@/services/customerService";
import { toast } from "sonner";
import apiClient from "@/lib/apiClient";

const ProfilePage: React.FC = () => {
  const { currentUser, refreshUser } = useAuth();
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // メール再送用
  const [resendingEmail, setResendingEmail] = useState(false);

  // 電話番号変更用
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);

  // 友達招待
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const [referralLoading, setReferralLoading] = useState(true);

  // クーポン
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponEnabled, setCouponEnabled] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadReferralInfo();
      loadCouponStatus();
    }
  }, [currentUser]);

  const loadReferralInfo = async () => {
    try {
      const info = await fetchReferralInfo();
      setReferralInfo(info);
    } catch (error) {
      console.error("Failed to load referral info:", error);
    } finally {
      setReferralLoading(false);
    }
  };

  const loadCouponStatus = async () => {
    try {
      const status = await fetchCouponStatus();
      setCouponEnabled(status.enabled);
    } catch (error) {
      console.error("Failed to load coupon status:", error);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label}をコピーしました`);
    } catch {
      toast.error("コピーに失敗しました");
    }
  };

  const shareToTwitter = () => {
    if (!referralInfo?.referral_code || !referralInfo?.referral_url) return;
    const text = `友達招待でポイントをゲット！招待コード: ${referralInfo.referral_code}`;
    const url = referralInfo.referral_url;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareToLine = () => {
    if (!referralInfo?.referral_code || !referralInfo?.referral_url) return;
    const text = `友達招待でポイントをゲット！招待コード: ${referralInfo.referral_code}\n${referralInfo.referral_url}`;
    window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(referralInfo.referral_url)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-theme-bg">
        <div className="pt-20 flex items-center justify-center">
          <p className="text-gray-700 [font-weight:var(--theme-font-weight-button)]">ログインしてください</p>
        </div>
      </div>
    );
  }

  const handleEmailUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newEmail) {
      toast.error("新しいメールアドレスを入力してください");
      return;
    }

    setLoading(true);
    try {
      const result = await updateEmail(newEmail);
      toast.success(result.message);
      setIsEditingEmail(false);
      setNewEmail("");
    } catch (error: any) {
      toast.error(error.response?.data?.error || "確認メールの送信に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingEmail(false);
    setNewEmail("");
  };

  // メール確認再送
  const handleResendVerificationEmail = async () => {
    if (!currentUser?.email) return;

    setResendingEmail(true);
    try {
      await apiClient.post('/customer/email_verifications', {
        email: currentUser.email
      });
      toast.success('確認メールを再送信しました。メールをご確認ください。');
    } catch (error: any) {
      toast.error(error.response?.data?.error || '確認メールの送信に失敗しました');
    } finally {
      setResendingEmail(false);
    }
  };

  // 電話番号変更：SMS送信
  const handleSendPhoneCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPhone) {
      toast.error("新しい電話番号を入力してください");
      return;
    }

    if (!newPhone.match(/^\d{10,11}$/)) {
      toast.error("電話番号は10桁または11桁の数字で入力してください");
      return;
    }

    setPhoneLoading(true);
    try {
      const result = await sendPhoneVerification(newPhone);
      toast.success(result.message);
      setCodeSent(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "認証コードの送信に失敗しました");
    } finally {
      setPhoneLoading(false);
    }
  };

  // 電話番号変更：コード確認
  const handleVerifyPhoneCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationCode) {
      toast.error("認証コードを入力してください");
      return;
    }

    if (!verificationCode.match(/^\d{6}$/)) {
      toast.error("認証コードは6桁の数字で入力してください");
      return;
    }

    setPhoneLoading(true);
    try {
      const result = await verifyPhoneCode(newPhone, verificationCode);
      toast.success(result.message);
      await refreshUser();
      setIsEditingPhone(false);
      setNewPhone("");
      setVerificationCode("");
      setCodeSent(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "認証コードの確認に失敗しました");
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleCancelPhoneEdit = () => {
    setIsEditingPhone(false);
    setNewPhone("");
    setVerificationCode("");
    setCodeSent(false);
  };

  // クーポンコード適用
  const handleRedeemCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponCode.trim()) {
      toast.error("クーポンコードを入力してください");
      return;
    }
    setCouponLoading(true);
    try {
      const result = await redeemCoupon(couponCode.trim());
      toast.success(result.message);
      setCouponCode("");
      await refreshUser();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "クーポンの適用に失敗しました");
    } finally {
      setCouponLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>マイページ</title>
        <meta property="og:title" content="マイページ" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="max-w-screen-lg mx-auto px-4 pb-4 sm:pb-8 pt-20">
          <div className="flex gap-8">
            {/* サイドバー - デスクトップのみ */}
            <div className="hidden lg:block">
              <UserSidebar />
            </div>

            {/* メインコンテンツ */}
            <section className="flex-1 min-w-0">
              {/* タイトルセクション */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                  <User className="h-5 w-5 text-theme-button" />
                </div>
                <div>
                  <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">マイページ</h1>
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">登録情報の確認・変更ができます</p>
                </div>
              </div>

              {/* コンテンツカード */}
              <div className="p-4 sm:p-6 bg-white border-theme rounded-theme-lg shadow-theme">
                <div className="space-y-6">
                  {/* メールアドレス */}
                  <div className="p-4 border-theme rounded-theme">
                    {!isEditingEmail ? (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                          <Mail className="w-4 h-4 text-theme-button" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1">メールアドレス</h3>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-base font-medium text-gray-800">{currentUser.email}</p>
                            {currentUser.email_verified ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-400 border-theme text-black text-xs [font-weight:var(--theme-font-weight-button)] rounded-theme">
                                <CheckCircle className="w-3 h-3" />
                                認証済み
                              </span>
                            ) : (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-theme-primary border-theme text-theme-button text-xs [font-weight:var(--theme-font-weight-button)] rounded-theme">
                                  <XCircle className="w-3 h-3" />
                                  未認証
                                </span>
                                <button
                                  onClick={handleResendVerificationEmail}
                                  disabled={resendingEmail}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-theme-primary border-theme text-theme-button text-xs [font-weight:var(--theme-font-weight-button)] rounded-theme hover:opacity-80 transition-colors disabled:opacity-50"
                                >
                                  <Send className="w-3 h-3" />
                                  {resendingEmail ? '送信中...' : '確認メールを再送'}
                                </button>
                              </div>
                            )}
                          </div>
                          {currentUser.pending_email && (
                            <div className="mt-2 p-2 bg-blue-100 border-2 border-blue-500 rounded-theme text-xs text-blue-700 font-medium">
                              <p className="[font-weight:var(--theme-font-weight-button)]">変更申請中: {currentUser.pending_email}</p>
                              <p className="mt-1">確認メールのリンクをクリックして変更を完了してください</p>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => setIsEditingEmail(true)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                        >
                          <Edit2 className="w-3 h-3" />
                          変更
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleEmailUpdate} className="space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                            <Mail className="w-4 h-4 text-theme-button" />
                          </div>
                          <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black">メールアドレスの変更</h3>
                        </div>

                        <div className="p-3 bg-blue-100 border-2 border-blue-500 rounded-theme">
                          <p className="text-xs text-blue-700 font-medium">
                            新しいメールアドレスに確認メールが送信されます。メール内のリンクをクリックして変更を完了してください。
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="new_email" className="[font-weight:var(--theme-font-weight-button)] text-black">新しいメールアドレス</Label>
                          <Input
                            id="new_email"
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="新しいメールアドレスを入力"
                            required
                            className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary"
                          />
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-theme-primary text-theme-button border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
                          >
                            {loading ? "送信中..." : "確認メールを送信"}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            disabled={loading}
                            className="px-4 py-2 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
                          >
                            キャンセル
                          </button>
                        </div>
                      </form>
                    )}
                  </div>

                  {/* 電話番号 */}
                  <div className="p-4 border-theme rounded-theme">
                    {!isEditingPhone ? (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                          <Phone className="w-4 h-4 text-theme-button" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1">電話番号</h3>
                          {currentUser.phone_number ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-base font-medium text-gray-800">{currentUser.phone_number}</p>
                              {currentUser.phone_verified ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-400 border-theme text-black text-xs [font-weight:var(--theme-font-weight-button)] rounded-theme">
                                  <CheckCircle className="w-3 h-3" />
                                  認証済み
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-theme-primary border-theme text-theme-button text-xs [font-weight:var(--theme-font-weight-button)] rounded-theme">
                                  <XCircle className="w-3 h-3" />
                                  未認証
                                </span>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm font-medium text-gray-500">未登録</p>
                          )}
                        </div>
                        <button
                          onClick={() => setIsEditingPhone(true)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                        >
                          <Edit2 className="w-3 h-3" />
                          変更
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                            <Phone className="w-4 h-4 text-theme-button" />
                          </div>
                          <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black">電話番号の変更</h3>
                        </div>

                        <div className="p-3 bg-blue-100 border-2 border-blue-500 rounded-theme">
                          <p className="text-xs text-blue-700 font-medium">
                            新しい電話番号にSMSで認証コードが送信されます。届いた6桁のコードを入力して変更を完了してください。
                          </p>
                        </div>

                        {!codeSent ? (
                          <form onSubmit={handleSendPhoneCode} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="new_phone" className="[font-weight:var(--theme-font-weight-button)] text-black">新しい電話番号</Label>
                              <Input
                                id="new_phone"
                                type="tel"
                                value={newPhone}
                                onChange={(e) => setNewPhone(e.target.value)}
                                placeholder="例: 09012345678"
                                pattern="\d{10,11}"
                                required
                                className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary"
                              />
                              <p className="text-xs font-medium text-gray-600">ハイフンなしで入力してください</p>
                            </div>

                            <div className="flex gap-2 pt-2">
                              <button
                                type="submit"
                                disabled={phoneLoading}
                                className="flex-1 px-4 py-2 bg-theme-primary text-theme-button border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
                              >
                                {phoneLoading ? "送信中..." : "認証コードを送信"}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelPhoneEdit}
                                disabled={phoneLoading}
                                className="px-4 py-2 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
                              >
                                キャンセル
                              </button>
                            </div>
                          </form>
                        ) : (
                          <form onSubmit={handleVerifyPhoneCode} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="verification_code" className="[font-weight:var(--theme-font-weight-button)] text-black">認証コード（6桁）</Label>
                              <Input
                                id="verification_code"
                                type="text"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value)}
                                placeholder="123456"
                                pattern="\d{6}"
                                maxLength={6}
                                required
                                className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary"
                              />
                              <p className="text-xs font-medium text-gray-600">{newPhone} に送信されたコードを入力してください</p>
                            </div>

                            <div className="flex gap-2 pt-2">
                              <button
                                type="submit"
                                disabled={phoneLoading}
                                className="flex-1 px-4 py-2 bg-theme-primary text-theme-button border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
                              >
                                {phoneLoading ? "確認中..." : "確認して変更する"}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelPhoneEdit}
                                disabled={phoneLoading}
                                className="px-4 py-2 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
                              >
                                キャンセル
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 生年月日 */}
                  <div className="flex items-start gap-3 p-4 border-theme rounded-theme">
                    <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-theme-button" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1">生年月日</h3>
                      {currentUser.birth_date ? (
                        <div className="space-y-1">
                          <p className="text-base font-medium text-gray-800">{currentUser.birth_date}</p>
                          {currentUser.age !== null && currentUser.age !== undefined && (
                            <p className="text-sm font-medium text-gray-600">年齢: {currentUser.age}歳</p>
                          )}
                          {currentUser.is_adult !== undefined && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs [font-weight:var(--theme-font-weight-button)] rounded-theme border-theme ${
                              currentUser.is_adult
                                ? "bg-green-400 text-black"
                                : "bg-red-400 text-black"
                            }`}>
                              {currentUser.is_adult ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  18歳以上
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-3 h-3" />
                                  18歳未満
                                </>
                              )}
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-gray-500">未登録</p>
                      )}
                    </div>
                  </div>

                </div>
              </div>

              {/* 友達招待セクション */}
              {referralInfo && referralInfo.enabled && (referralInfo.campaign_active || (referralInfo.total_invited ?? 0) > 0 || (referralInfo.total_points_earned ?? 0) > 0) && (
                <div id="referral" className="mt-6 p-4 sm:p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-theme rounded-theme-lg shadow-theme scroll-mt-24">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                      <Users className="h-5 w-5 text-theme-button" />
                    </div>
                    <div>
                      <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">友達招待</h2>
                      <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">
                        {referralInfo.campaign_active
                          ? `招待した友達が${
                              referralInfo.conversion_point === 'registration' ? '会員登録（メール認証）' :
                              referralInfo.conversion_point === 'first_purchase' ? '初回購入' :
                              '電話番号認証'
                            }を完了するとお互いにポイントがもらえます`
                          : "現在開催中のキャンペーンはありません"}
                      </p>
                    </div>
                  </div>

                  {referralInfo.campaign_active && (
                    <>
                      {/* 報酬説明 */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="p-3 bg-white border-theme rounded-theme">
                          <div className="flex items-center gap-2 mb-1">
                            <Gift className="w-4 h-4 text-purple-600" />
                            <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">あなたに</span>
                          </div>
                          <p className="text-xl [font-weight:var(--theme-font-weight)] text-purple-600">{(referralInfo.inviter_points ?? 0).toLocaleString()} pt</p>
                        </div>
                        <div className="p-3 bg-white border-theme rounded-theme">
                          <div className="flex items-center gap-2 mb-1">
                            <Gift className="w-4 h-4 text-pink-600" />
                            <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">友達に</span>
                          </div>
                          <p className="text-xl [font-weight:var(--theme-font-weight)] text-pink-600">{(referralInfo.invitee_points ?? 0).toLocaleString()} pt</p>
                        </div>
                      </div>

                      {/* 招待コード */}
                      <div className="space-y-3">
                        <div className="p-3 bg-white border-theme rounded-theme">
                          <label className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600 block mb-1">招待コード</label>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-lg [font-weight:var(--theme-font-weight)] text-black tracking-widest">{referralInfo.referral_code ?? ""}</code>
                            <button
                              onClick={() => copyToClipboard(referralInfo.referral_code ?? "", "招待コード")}
                              className="p-2 bg-theme-primary border-theme rounded-theme shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div className="p-3 bg-white border-theme rounded-theme">
                          <label className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600 block mb-1">招待URL</label>
                          <div className="flex items-center gap-2">
                            <span className="flex-1 text-xs font-medium text-gray-700 truncate">{referralInfo.referral_url ?? ""}</span>
                            <button
                              onClick={() => copyToClipboard(referralInfo.referral_url ?? "", "招待URL")}
                              className="p-2 bg-theme-primary border-theme rounded-theme shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* シェアボタン */}
                        <div className="flex gap-2">
                          <button
                            onClick={shareToLine}
                            className="flex-1 flex items-center justify-center gap-2 py-2 bg-[#00B900] text-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                          >
                            <span>LINEで共有</span>
                          </button>
                          <button
                            onClick={shareToTwitter}
                            className="flex-1 flex items-center justify-center gap-2 py-2 bg-black text-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                          >
                            <span>Xで共有</span>
                          </button>
                        </div>
                      </div>

                      {/* 残り招待可能数（上限設定時のみ表示） */}
                      {referralInfo.max_invitations_per_inviter != null && (
                        <div className="mt-3 p-3 bg-white border-theme rounded-theme flex items-center justify-between">
                          <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">残り招待可能数</span>
                          {referralInfo.remaining_invitations === 0 ? (
                            <span className="text-sm [font-weight:var(--theme-font-weight)] text-red-500">上限に達しました</span>
                          ) : (
                            <span className="text-sm [font-weight:var(--theme-font-weight)] text-black">
                              {referralInfo.remaining_invitations} / {referralInfo.max_invitations_per_inviter} 人
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* 実績 */}
                  <div className={referralInfo.campaign_active ? "mt-4 pt-4 border-t-[length:var(--theme-border-width)] border-black/20" : ""}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="[font-weight:var(--theme-font-weight-button)] text-gray-600">これまでの招待実績</span>
                      <span className="[font-weight:var(--theme-font-weight)] text-black">
                        {referralInfo.total_invited ?? 0}人招待 / {(referralInfo.total_points_earned ?? 0).toLocaleString()} pt獲得
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* クーポンコード入力セクション */}
              {couponEnabled && (
              <div id="coupon" className="mt-6 p-4 sm:p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-theme rounded-theme-lg shadow-theme scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                    <Ticket className="h-5 w-5 text-theme-button" />
                  </div>
                  <div>
                    <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">クーポンコード</h2>
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">
                      クーポンコードを入力してポイントを獲得
                    </p>
                  </div>
                </div>

                <form onSubmit={handleRedeemCoupon} className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="コードを入力"
                    className="flex-1 font-mono text-lg tracking-widest bg-white border-theme"
                    disabled={couponLoading}
                  />
                  <Button
                    type="submit"
                    disabled={couponLoading || !couponCode.trim()}
                    className="bg-theme-primary text-theme-button border-theme shadow-theme-sm hover:shadow-theme-hover"
                  >
                    {couponLoading ? "適用中..." : "適用"}
                  </Button>
                </form>
              </div>
              )}

              {/* 注意事項 */}
              <div className="mt-6 p-4 bg-blue-100 border-2 border-blue-500 rounded-theme">
                <h3 className="text-sm [font-weight:var(--theme-font-weight)] text-blue-900 mb-2">登録情報の変更について</h3>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside font-medium">
                  <li>メールアドレス変更は、確認メールのリンクをクリックすることで完了します</li>
                  <li>確認メールが届かない場合は、迷惑メールフォルダをご確認ください</li>
                  <li>電話番号変更は、新しい番号にSMSで送信される認証コードの入力が必要です</li>
                  <li>生年月日の変更が必要な場合は、お問い合わせフォームよりご連絡ください</li>
                </ul>
              </div>

            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
